import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Handshake, Users, Tag, TrendingUp, DollarSign, CheckCircle2, Clock, Percent, Loader2 } from "lucide-react";
import AppLayout from "@/layouts/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/ds";

const money = (n: number | null | undefined) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const date = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : "—");

const STATUS_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  active: "green", paid: "green", pending: "amber", mixed: "blue", past_due: "red",
  cancelled: "red", not_converted: "slate", no_payments_yet: "slate", disabled: "red", inactive: "slate",
};

export default function AgencyPartners() {
  const { user, isPlatformAdmin } = useAuth();
  const [summary, setSummary] = useState<any>(null);
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    Promise.all([api.agencySummary(), api.agencyReferrals()])
      .then(([s, r]) => { setSummary(s); setRows(r); })
      .catch((e) => setErr(e?.response?.data?.error?.message || "Failed to load"));
  }, [isPlatformAdmin]);

  if (user === null) return null;
  if (!isPlatformAdmin) return <Navigate to="/app/dashboard" replace />;

  const cards = summary ? [
    { label: "Agency Partners", value: summary.agency_partners, icon: Handshake },
    { label: "Referral Codes", value: summary.referral_codes, icon: Tag },
    { label: "Referrals", value: summary.referrals, icon: Users },
    { label: "Conversions", value: summary.conversions, icon: CheckCircle2 },
    { label: "Partner-Attributed Customers", value: summary.partner_attributed_customers, icon: Users },
    { label: "Recurring Revenue (MRR)", value: money(summary.recurring_revenue), icon: TrendingUp },
    { label: "Commissions Earned", value: money(summary.commissions_earned), icon: DollarSign },
    { label: "Commissions Pending", value: money(summary.commissions_pending), icon: Clock },
    { label: "Commissions Paid", value: money(summary.commissions_paid), icon: CheckCircle2 },
    { label: "Discounts Given", value: `${money(summary.discounts_given?.setup)} setup · ${money(summary.discounts_given?.monthly)}/mo`, icon: Percent },
  ] : [];

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="agency-partners-page">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Agency Partners</h1>
          <p className="mt-1 text-sm text-slate-500">Referral attribution, conversions and commissions. Historical records are retained even if a code or customer becomes inactive.</p>
        </div>

        {err && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="agency-error">{err}</div>}

        {!summary && !err && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

        {summary && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="agency-metrics">
            {cards.map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><c.icon className="h-4 w-4 text-blue-500" /> {c.label}</div>
                <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {rows && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-3"><h2 className="text-sm font-semibold text-slate-800">Referrals</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px] text-left text-sm" data-testid="referrals-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    {["Agency Partner", "Code", "Referred business", "Referral date", "Attribution exp.", "Conversion", "Plan", "Setup fee", "Setup disc.", "Monthly", "Monthly disc.", "Eligible collected", "Comm. %", "Commission", "Payment", "Sub status"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 && <tr><td colSpan={16} className="px-3 py-8 text-center text-slate-400">No referrals yet.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.referral_id} className="hover:bg-slate-50" data-testid={`referral-row-${r.referral_id}`}>
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">{r.agency_partner}</td>
                      <td className="whitespace-nowrap px-3 py-2"><span className="font-mono text-xs">{r.referral_code}</span>{r.code_status === "inactive" && <span className="ml-1 text-[10px] text-red-500">(disabled)</span>}</td>
                      <td className="whitespace-nowrap px-3 py-2">{r.referred_business}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">{date(r.referral_date)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">{r.attribution_expiration === "Lifetime" ? "Lifetime" : date(r.attribution_expiration)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">{date(r.conversion_date)}</td>
                      <td className="whitespace-nowrap px-3 py-2 capitalize">{r.plan}</td>
                      <td className="whitespace-nowrap px-3 py-2">{money(r.setup_fee)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-emerald-600">{r.setup_discount_percent}% · {money(r.setup_discount_amount)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{money(r.monthly_subscription)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-emerald-600">{r.monthly_discount_percent}% · {money(r.monthly_discount_amount)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{money(r.eligible_collected_revenue)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{r.commission_percent}%</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold text-slate-900">{money(r.commission_amount)}</td>
                      <td className="whitespace-nowrap px-3 py-2"><StatusBadge tone={STATUS_TONE[r.payment_status] || "slate"}>{(r.payment_status || "unknown").replace(/_/g, " ")}</StatusBadge></td>
                      <td className="whitespace-nowrap px-3 py-2"><StatusBadge tone={STATUS_TONE[r.customer_subscription_status] || "slate"}>{(r.customer_subscription_status || "unknown").replace(/_/g, " ")}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
